import React from "react";
import { Container } from "../components/ui/Container";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/Button";
import { trackTryKnoothClick } from "../lib/analytics";

const Tag = ({ children }) => {
  return (
    <span className="text-[16px] font-normal leading-[100%] text-[#412F14] bg-white border border-black/10 rounded px-[6px] py-[4px] w-fit">
      {children}
    </span>
  );
};

const CheckIcon = ({ className }) => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.707-9.707a1 1 0 0 0-1.414-1.414L9 10.172 7.707 8.879a1 1 0 1 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4Z"
      />
    </svg>
  );
};

const Bullet = ({ children }) => {
  return (
    <li className="flex items-start gap-2 text-[14px] md:text-[16px] font-normal leading-[140%] text-[#412F14]/70">
      <span className="flex-shrink-0 text-[#412F14]/50 mt-[2px]">
        <CheckIcon />
      </span>
      <span>{children}</span>
    </li>
  );
};

const Section = ({ tag, children }) => {
  return (
    <section className="mt-[32px] border-t border-black/10 pt-[80px] first:mt-0 first:border-t-0 first:pt-0">
      <Tag>{tag}</Tag>
      <div className="mt-[36px] text-[16px] md:text-[18px] leading-[160%] text-[#666666]">
        {children}
      </div>
    </section>
  );
};

const PrivacyPolicy = () => {
  return (
    <div id="top" className="min-h-screen bg-[#FBFBFD]">
      <main className="pt-[280px]">
        <Container>
          <div className="text-center">
            <h1
              className="text-6xl md:text-8xl lg:text-[96px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text pb-2"
              style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
            >
              Privacy Policy
              <br />
              for knooth
            </h1>
            <p className="mt-[16px] text-[14px] md:text-[16px] font-normal leading-[140%] text-[#666666]">
              Last updated: 21.01.2026
            </p>
          </div>

          <div className="mt-[80px] max-w-[1000px] mx-auto space-y-0">
            <Section tag="Introduction">
              <p className="font-semibold text-[#666666]">
                Welcome to knooth ("we", "us", or "our").
                <br />
                Your privacy is important to us. This Privacy Policy explains how we handle
                information when you use the knooth application (the "App").
              </p>

              <div className="mt-[24px]">
                <p className="font-semibold text-[#666666]">Short summary</p>
                <ul className="mt-[12px] space-y-3">
                  <Bullet>
                    knooth collects limited, anonymized usage analytics using a privacy-first
                    analytics provider to help improve performance and features.
                  </Bullet>
                  <Bullet>Your content stays on your device.</Bullet>
                  <Bullet>
                    Apple may provide technical crash reports if you opt in to share diagnostic
                    data.
                  </Bullet>
                </ul>
              </div>
            </Section>

            <Section tag="Information We Collect">
              <p className="font-semibold text-[#666666]">knooth collects:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Limited, anonymized usage analytics via a privacy-first analytics provider (to improve performance and features)</Bullet>
              </ul>
              <p className="mt-[16px] font-semibold text-[#666666]">knooth does not collect or process:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Personal information (such as name, email, phone number, or identifiers)</Bullet>
                <Bullet>Location data</Bullet>
                <Bullet>Your content (recordings, assets, projects, exports)</Bullet>
              </ul>
              <p className="mt-[16px]">
                Your creative content stays on your device. Analytics, where used, is intended to
                be anonymous and limited to understanding aggregate usage.
              </p>
            </Section>

            <Section tag="On-Device AI Processing">
              <p className="font-semibold text-[#666666]">knooth includes on-device AI features to assist with:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Generating captions</Bullet>
                <Bullet>Cleaning up speech disfluencies and filler words</Bullet>
              </ul>
              <p className="font-semibold text-[#666666] mt-[16px]">How AI works in knooth</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>All AI processing runs entirely on your device</Bullet>
                <Bullet>No data is sent to external servers</Bullet>
                <Bullet>No cloud inference is used</Bullet>
                <Bullet>No AI data is shared with third parties</Bullet>
              </ul>
              <p className="font-semibold text-[#666666] mt-[16px]">AI Features</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>All AI runs on-device — no cloud processing</Bullet>
                <Bullet>Supports 25+ languages</Bullet>
              </ul>
              <p className="mt-[16px]">
                All functionality works locally on your device and does not send data to us.
              </p>
            </Section>

            <Section tag="Crash Reports & Diagnostic Logs">
              <p>
                knooth collects anonymous usage and crash diagnostics to improve app performance
                and reliability.
              </p>
              <p className="mt-[16px]">
                Apple may also provide anonymized crash reports if you’ve enabled diagnostic
                sharing on your device.
              </p>
              <p className="mt-[16px]">
                No personal data or app content is collected, and diagnostics are never used for
                tracking or profiling.
              </p>
            </Section>

            <Section tag="Your Content Stays on Your Device">
              <p>
                Everything you create with knooth — such as recordings, notes, designs, or files
                — stays on your device unless you explicitly export, share, or back up files
                yourself. That information is controlled by you and your operating system.
              </p>
              <p className="mt-[16px]">We do not receive, access, or store any user-generated content.</p>
            </Section>

            <Section tag="Ads & Third-Party Services">
              <p className="font-semibold text-[#666666]">Advertising</p>
              <p>knooth does not show ads or sell your personal information.</p>

              <p className="font-semibold text-[#666666] mt-[16px]">Analytics</p>
              <p>
                We use a privacy-first analytics provider to collect limited, anonymized usage
                data in aggregate. No personal data, identifiers, or user content is collected
                through analytics.
              </p>
              <p className="mt-[16px]">
                Any future changes to analytics or data practices will be disclosed clearly in
                updates to this policy.
              </p>
            </Section>

            <Section tag="Cookies & Web Tracking">
              <p>
                The App itself does not use cookies. Our website may use cookies or similar
                technologies (for example, via analytics) to understand traffic and improve the
                experience.
              </p>
              <p className="mt-[16px]">
                If you visit our website, basic server logs may also be collected by our hosting
                provider. These logs are used for security and operational purposes.
              </p>
            </Section>

            <Section tag="Children's Privacy">
              <p>
                knooth is rated 4+ on the App Store. Because knooth does not collect personal
                information, it does not knowingly collect any information from children or
                anyone else.
              </p>
            </Section>

            <Section tag="Data Security">
              <p>We do not collect or store personal information on our servers.</p>
              <p className="mt-[16px]">Your data remains under your control on your device.</p>
              <p className="mt-[16px]">Crash reports provided by Apple are stored securely by Apple.</p>
            </Section>

            <Section tag="Your Rights">
              <p className="font-semibold text-[#666666]">Since knooth does not collect personally identifiable information:</p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>There is no personal data to access, correct, export, or delete</Bullet>
                <Bullet>No automated profiling is done</Bullet>
                <Bullet>You can manage crash data sharing through your Apple device settings</Bullet>
              </ul>
            </Section>

            <Section tag="Changes to This Policy">
              <p className="font-semibold text-[#666666]">
                If we ever change our data practices or add optional features that collect data,
                we will:
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>Update this Privacy Policy</Bullet>
                <Bullet>Clearly explain what is collected</Bullet>
                <Bullet>Offer choices wherever possible</Bullet>
              </ul>
            </Section>

            <Section tag="Contact Us">
              <p className="font-semibold text-[#666666]">
                If you have questions about this policy or app behavior, reach us at:
              </p>
              <ul className="mt-[12px] space-y-3">
                <Bullet>
                  Email:&nbsp;
                  <a
                    href="mailto:support@knooth.com"
                    className="text-[#31220D] hover:underline"
                  >
                    support@knooth.com
                  </a>
                </Bullet>
                <Bullet>
                  Website:&nbsp;
                  <a
                    href="https://www.knooth.com"
                    target="_blank"
                    rel="noreferrer"
                    className="text-[#31220D] hover:underline"
                  >
                    https://www.knooth.com
                  </a>
                </Bullet>
              </ul>
            </Section>
          </div>

          {/* Closing statement + CTA */}
          <div className="mt-[120px] text-center pb-[140px]">
            <h2
              className="text-4xl md:text-5xl lg:text-[56px] font-[900] tracking-[-0.02em] leading-none text-transparent bg-clip-text"
              style={{ backgroundImage: "linear-gradient(180deg, #31220D 57%, #666666 100%)" }}
            >
              Your data stays on your device.
              <br />
              We don&apos;t collect personal information.
            </h2>

            <div className="mt-[40px] flex flex-col items-center gap-[10px]">
              <Button
                href="https://apps.apple.com/app/knooth/id6756659300?mt=12"
                target="_blank"
                rel="noreferrer"
                data-analytics="try-knooth"
                data-location="privacy-cta"
                onClick={() => trackTryKnoothClick("privacy-cta")}
                className="w-[176px] h-[44px] px-6 py-[10px] rounded-full text-white font-bold text-[24px] hover:shadow-lg transition-all border-none leading-none flex items-center justify-center"
                style={{ background: "linear-gradient(180deg, #2F00EC 57%, #C8A5FF 100%)" }}
              >
                Try knooth
              </Button>
              <p className="text-[14px] font-normal leading-[1.4] text-[#31220D]">1 week free for new subscribers.</p>
            </div>
          </div>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export { PrivacyPolicy };
